'use client';

import { Check, Pipette, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { cn } from '@/lib/utils/cn';

const PICKER_WIDTH = 280;
const PICKER_ESTIMATED_HEIGHT = 356;
const VIEWPORT_MARGIN = 12;
const PICKER_GAP = 8;

type PickerPosition = {
  left: number;
  top: number;
  width: number;
  maxHeight: number;
};

type EyeDropperConstructor = new () => {
  open: () => Promise<{ sRGBHex: string }>;
};

const SWATCHES = [
  '#ffffff', '#d1d5db', '#111827', '#000000', '#5b35ff', '#7c3aed',
  '#ec4899', '#ef4444', '#f97316', '#facc15', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6', '#a78bfa', '#fb7185',
] as const;

function normalizeHex(value: string): string | null {
  const raw = value.trim().replace(/^#/, '');
  if (/^[0-9a-f]{3}$/i.test(raw)) return `#${raw.split('').map((c) => c + c).join('')}`.toLowerCase();
  if (/^[0-9a-f]{6}$/i.test(raw)) return `#${raw.toLowerCase()}`;
  return null;
}

function hexToHsv(hex: string) {
  const normalized = normalizeHex(hex) ?? '#ffffff';
  const r = parseInt(normalized.slice(1, 3), 16) / 255;
  const g = parseInt(normalized.slice(3, 5), 16) / 255;
  const b = parseInt(normalized.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const delta = max - min;
  let h = 0;
  if (delta) {
    if (max === r) h = 60 * (((g - b) / delta) % 6);
    else if (max === g) h = 60 * ((b - r) / delta + 2);
    else h = 60 * ((r - g) / delta + 4);
  }
  if (h < 0) h += 360;
  return { h, s: max === 0 ? 0 : delta / max, v: max };
}

function hsvToHex(h: number, s: number, v: number) {
  const c = v * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = v - c;
  let [r, g, b] = [0, 0, 0];
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return `#${[r, g, b].map((n) => Math.round((n + m) * 255).toString(16).padStart(2, '0')).join('')}`;
}

export function EditorColorPicker({
  value,
  onChange,
  allowTransparent = false,
}: {
  value: string;
  onChange: (value: string) => void;
  allowTransparent?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(normalizeHex(value) ?? '#ffffff');
  const [hsv, setHsv] = useState(() => hexToHsv(draft));
  const [position, setPosition] = useState<PickerPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const width = Math.min(PICKER_WIDTH, window.innerWidth - VIEWPORT_MARGIN * 2);
    const maxHeight = Math.max(220, window.innerHeight - VIEWPORT_MARGIN * 2);
    const measuredHeight = popupRef.current?.offsetHeight ?? PICKER_ESTIMATED_HEIGHT;
    const height = Math.min(measuredHeight, maxHeight);
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.right - width),
      window.innerWidth - width - VIEWPORT_MARGIN,
    );

    const spaceBelow = window.innerHeight - rect.bottom - VIEWPORT_MARGIN;
    const spaceAbove = rect.top - VIEWPORT_MARGIN;
    const preferredTop = spaceBelow >= height || spaceBelow >= spaceAbove
      ? rect.bottom + PICKER_GAP
      : rect.top - PICKER_GAP - height;
    const top = Math.min(
      Math.max(VIEWPORT_MARGIN, preferredTop),
      window.innerHeight - height - VIEWPORT_MARGIN,
    );

    setPosition({ left, top, width, maxHeight });
  }, []);

  useEffect(() => {
    const normalized = normalizeHex(value);
    if (!normalized) return;
    setDraft(normalized);
    setHsv(hexToHsv(normalized));
  }, [value]);

  useEffect(() => {
    if (!open) return;
    const close = (event: PointerEvent) => {
      const target = event.target as Node;
      if (!rootRef.current?.contains(target) && !popupRef.current?.contains(target)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    window.addEventListener('pointerdown', close);
    window.addEventListener('keydown', escape);
    return () => {
      window.removeEventListener('pointerdown', close);
      window.removeEventListener('keydown', escape);
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPosition(null);
      return;
    }

    updatePosition();
    const frame = window.requestAnimationFrame(updatePosition);
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open, updatePosition]);

  const commitHsv = (next: typeof hsv) => {
    setHsv(next);
    const hex = hsvToHex(next.h, next.s, next.v);
    setDraft(hex);
    onChange(hex);
  };

  const pickSaturation = (event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const s = Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width));
    const v = 1 - Math.min(1, Math.max(0, (event.clientY - rect.top) / rect.height));
    commitHsv({ ...hsv, s, v });
  };

  const pickFromScreen = async () => {
    const EyeDropper = (window as typeof window & { EyeDropper?: EyeDropperConstructor }).EyeDropper;
    if (!EyeDropper) return;
    try {
      const result = await new EyeDropper().open();
      const color = normalizeHex(result.sRGBHex);
      if (!color) return;
      setDraft(color);
      setHsv(hexToHsv(color));
      onChange(color);
    } catch {
      // The browser rejects the promise when the user cancels the eyedropper.
    }
  };

  const supportsEyeDropper = typeof window !== 'undefined' && 'EyeDropper' in window;

  const picker = open && position ? createPortal(
    <div
      ref={popupRef}
      role="dialog"
      aria-label="Выбор цвета"
      className="fixed z-[100] overflow-y-auto rounded-xl border border-white/10 bg-[#17121f] p-3 shadow-2xl shadow-black/60"
      style={{
        left: position.left,
        top: position.top,
        width: position.width,
        maxHeight: position.maxHeight,
      }}
    >
      <div className="mb-2 flex items-center justify-between">
        <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-white/45">Цвет</span>
        <div className="flex items-center gap-1">
          {supportsEyeDropper && (
            <button
              type="button"
              onClick={pickFromScreen}
              aria-label="Взять цвет с экрана"
              title="Взять цвет с экрана"
              className="rounded-md p-1 text-white/45 transition hover:bg-white/10 hover:text-white"
            >
              <Pipette className="h-3.5 w-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label="Закрыть выбор цвета"
            className="rounded-md p-1 text-white/40 transition hover:bg-white/10 hover:text-white"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      <div
        role="group"
        aria-label="Насыщенность и яркость"
        tabIndex={0}
        onPointerDown={(event) => {
          event.currentTarget.setPointerCapture(event.pointerId);
          pickSaturation(event);
        }}
        onPointerMove={(event) => event.buttons === 1 && pickSaturation(event)}
        onKeyDown={(event) => {
          const delta = event.shiftKey ? 0.1 : 0.02;
          if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
            event.preventDefault();
            commitHsv({ ...hsv, s: Math.min(1, Math.max(0, hsv.s + (event.key === 'ArrowRight' ? delta : -delta))) });
          } else if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
            event.preventDefault();
            commitHsv({ ...hsv, v: Math.min(1, Math.max(0, hsv.v + (event.key === 'ArrowUp' ? delta : -delta))) });
          }
        }}
        className="relative h-32 cursor-crosshair overflow-hidden rounded-lg ring-1 ring-white/10 focus:outline-none focus:ring-brand-500"
        style={{ backgroundColor: `hsl(${hsv.h} 100% 50%)` }}
      >
        <div className="absolute inset-0 bg-gradient-to-r from-white to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
        <span
          className="absolute h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white shadow"
          style={{ left: `${hsv.s * 100}%`, top: `${(1 - hsv.v) * 100}%`, backgroundColor: draft }}
        />
      </div>

      <input
        type="range"
        min={0}
        max={359}
        value={Math.round(hsv.h)}
        onChange={(event) => commitHsv({ ...hsv, h: Number(event.target.value) })}
        aria-label="Оттенок"
        className="editor-hue-slider mt-2 h-2 w-full cursor-pointer appearance-none rounded-full"
      />

      <div className="mt-3 grid grid-cols-8 gap-1.5">
        {SWATCHES.map((color) => (
          <button
            key={color}
            type="button"
            aria-label={color}
            onClick={() => {
              setDraft(color);
              setHsv(hexToHsv(color));
              onChange(color);
            }}
            className="relative h-6 rounded-md border border-white/15 transition hover:scale-110 focus:outline-none focus:ring-2 focus:ring-brand-500"
            style={{ backgroundColor: color }}
          >
            {draft === color && <Check className={cn('absolute inset-0 m-auto h-3.5 w-3.5', color === '#ffffff' ? 'text-black' : 'text-white')} />}
          </button>
        ))}
      </div>

      <div className="mt-3 flex gap-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onBlur={() => {
            const normalized = normalizeHex(draft);
            if (!normalized) return setDraft(normalizeHex(value) ?? '#ffffff');
            setDraft(normalized);
            setHsv(hexToHsv(normalized));
            onChange(normalized);
          }}
          onKeyDown={(event) => event.key === 'Enter' && event.currentTarget.blur()}
          aria-label="HEX цвет"
          className="h-9 min-w-0 flex-1 rounded-lg border border-white/10 bg-white/5 px-2 text-xs font-semibold uppercase text-white outline-none focus:border-brand-500"
        />
        {allowTransparent && (
          <button
            type="button"
            onClick={() => onChange('transparent')}
            className={cn(
              'rounded-lg border px-3 text-[10px] font-bold transition',
              value === 'transparent' ? 'border-brand-500 bg-brand-500/15 text-white' : 'border-white/10 text-white/50 hover:text-white'
            )}
          >
            Нет
          </button>
        )}
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <div ref={rootRef}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex w-full items-center gap-2 rounded-lg border border-white/10 bg-white/5 px-2 py-1.5 text-left transition hover:border-white/20 focus:border-brand-500 focus:outline-none"
      >
        <span
          className="h-6 w-8 shrink-0 rounded-md border border-white/15 shadow-inner"
          style={{
            background:
              value === 'transparent'
                ? 'linear-gradient(45deg,#fff 25%,#bbb 25%,#bbb 50%,#fff 50%,#fff 75%,#bbb 75%) 0 0/8px 8px'
                : draft,
          }}
        />
        <span className="min-w-0 flex-1 truncate text-[11px] font-semibold uppercase text-white/70">{value}</span>
        <Pipette className="h-3.5 w-3.5 text-white/40" />
      </button>
      {picker}
    </div>
  );
}
